/**
 * The editor.
 *
 * ⚠️ EVERY CHANGE GOES THROUGH `@ksa/model`'s OPS. Nothing here reaches into the document and
 * mutates it: `setProp`, `addBlock`, `movePage` and the rest are the only way it changes, which is
 * what makes the AI's edits and a customer's edits literally the same operations (see `ops.ts`).
 *
 * ⚠️ AND THE PREVIEW IS THE RENDERER, not a facsimile of it. `<SitePage>` here is the same component
 * that publish-time SSR calls, reading the same stylesheet the publisher writes to R2. What you see
 * is what publishes — by construction rather than by care.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import {
  Button,
  Control,
  Field,
  Input,
  ListItem,
  Menu,
  Notice,
  Select,
  Spinner,
  loadPlaceholderArt,
  pickPlaceholder,
  useLang,
  type FieldContext,
} from '@ksa/ui'
import {
  BLOCK_WIDTHS,
  addBlock,
  defaultRow,
  addPage,
  duplicateBlock,
  effectiveWidth,
  getBlock,
  getBlockDef,
  moveBlock,
  movePage,
  pageBlockDefs,
  refsEqual,
  removeBlock,
  removePage,
  loadSampleSite,
  resolve,
  sampleFor,
  setDefaultLang,
  setPageSlug,
  setPageTitle,
  setProp,
  setTemplate,
  setVariant,
  setWidth,
  slugState,
  type BlockRef,
  type ImageRef,
  type Lang,
  type Site,
} from '@ksa/model'
import { SitePage, TEMPLATES } from '@ksa/render'
import { ApiFailure, api, publishedUrl, snapshotIsCurrent } from '../api.ts'
import { clearDemo, readDemo, writeDemo } from '../demo.ts'
import { LanguageFill } from '../LanguageFill.tsx'
import { fieldTargets } from '../fieldTargets.ts'
import { listTargets } from '../listTargets.ts'
import { prepareImage } from '../upload.ts'
import { useLoad } from '../useLoad.ts'
import { useTitle } from '../useTitle.ts'
import { holdReload } from '../useDeployWatch.ts'
import { usePublishEditorChrome, type Pane, type SaveState } from '../editorChrome.tsx'
import { PublishWizard } from './PublishWizard.tsx'
import { Dialog } from '../Dialog.tsx'
import './editor.css'

export function Editor() {
  const { id = '' } = useParams()
  const { t, lang } = useLang()
  const [loaded] = useLoad((signal) => api.site(id, signal), [id])

  if (loaded.state === 'loading') {
    return (
      <div className="u-page u-section" style={{ textAlign: 'center' }}>
        <Spinner label={t('Opening your site', 'جارٍ فتح موقعك')} />
      </div>
    )
  }
  if (loaded.state === 'error') {
    return (
      <div className="u-page u-section u-stack">
        <Notice tone="error">{lang === 'ar' ? loaded.failure.text.ar : loaded.failure.text.en}</Notice>
        <div>
          <Link to="/sites">
            <Button variant="quiet">{t('Back to your sites', 'العودة إلى مواقعك')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <Workspace
      id={id}
      initial={loaded.data.document}
      handle={loaded.data.handle}
      // ⚠️ ASKED ONCE, HERE, AGAINST THE RECORD THE SERVER SENT. The answer stops being true the
      // moment somebody types, so the editor cannot keep asking it; what it carries forward is the
      // one fact it can — whether the snapshot was current when the site was opened. `snapshotIsCurrent`
      // is shared with the site card, because this rule was written twice and both copies raced.
      published={snapshotIsCurrent(loaded.data)}
    />
  )
}

/**
 * One of the landing page's examples, opened in the real editor and belonging to nobody.
 *
 * ⚠️ THE SAME `Workspace` AS A REAL SITE, NOT A CUT-DOWN PREVIEW OF IT. A demonstration built out of a
 * second, simpler editor would demonstrate the second editor: the section that cannot be moved, the
 * field that opens a different drawer, the colour switcher that is not really there. Every difference
 * between this and a signed-in customer's screen is a difference somebody discovers AFTER paying, so
 * there is exactly one — where the document goes when it changes.
 *
 * ⚠️ AND IT NEEDS NO SESSION. `/demo/:sample` makes no authenticated request at all, so a signed-out
 * visitor never trips the sign-in bounce in `api.ts`.
 */
export function DemoEditor({ signedIn }: { signedIn: boolean }) {
  const { sample = '' } = useParams()
  return <LocalEditor id={sample} path={`/demo/${sample}`} signedIn={signedIn} kind="sample" />
}

/**
 * A site the model just wrote for somebody with no account.
 *
 * ⚠️ IT IS THE SAME WORKSPACE AS AN EXAMPLE, AND ALMOST NONE OF THIS IS NEW. Building without signing
 * in needed a place to put the result, and that place already existed: a document in this tab, the
 * real editor over it, a sticky bar saying it is not saved, and one button that turns it into a site.
 * The only difference from an example is where the document came from — which is why the two share a
 * component rather than resembling one another.
 *
 * ⚠️ AND IT IS ONLY EVER READ FROM STORAGE. The landing page writes the document before it navigates
 * (`destinationFor`), because there is nothing to fetch: the server did not keep a copy. Arriving
 * here with a draft id this browser has never heard of — a link sent to a friend, a different
 * machine, a draft that aged past thirty days (`demo.ts`) — is therefore an ordinary state and not an
 * error to apologise for.
 */
export function DraftEditor({ signedIn }: { signedIn: boolean }) {
  const { draft = '' } = useParams()
  return <LocalEditor id={draft} path={`/draft/${draft}`} signedIn={signedIn} kind="draft" />
}

function LocalEditor({
  id: localId,
  path,
  signedIn,
  kind,
}: {
  id: string
  path: string
  signedIn: boolean
  kind: 'sample' | 'draft'
}) {
  const sampleId = localId
  const { t } = useLang()
  const sample = kind === 'sample' ? sampleFor(sampleId) : undefined

  /**
   * ⚠️ WHAT WAS ALREADY EDITED WINS, AND IT COSTS NO CHUNK. Somebody who moved a section, refreshed,
   * and got the example back would rightly conclude the editor does not work — and their version is
   * already in this tab, so there is nothing to fetch for it.
   *
   * ⚠️ READ ONCE, IN STATE. `readDemo` is a parse of whatever the browser stored; doing it in the
   * render body would hand the editor a new object on every keystroke.
   */
  /*
   * ⚠️ KEYED ON THE ID FROM THE URL, NOT ON A SAMPLE. It read `sample.id` while this component only
   * ever served the five examples, and a draft has no sample — so every generated site loaded as
   * "that site is not there any more" while sitting in the store under the very key in the
   * address bar. The URL is what says which document this is; there is nothing else to ask.
   */
  const [stored] = useState(() => (sampleId ? readDemo(sampleId, kind) : null))

  /**
   * ⚠️ THE DOCUMENT IS FETCHED, NOT IMPORTED — see `loadSampleSite`. Five whole websites are ~30 kB
   * of copy, and the page that OFFERS them renders none of it. The wait is one chunk, and the editor
   * shows the same "opening" state it shows for a real site, because that is what is happening.
   *
   * ⚠️ AND IT IS BUILT ONCE. `SITES[id]()` mints fresh ids; a document rebuilt on a re-render would
   * change every block id underneath a selection that points at one.
   */
  const [loaded] = useLoad(
    () => (stored ? Promise.resolve(stored.document) : kind === 'sample' ? loadSampleSite(sampleId) : Promise.resolve(null)),
    [sampleId, kind],
  )

  // Stable while nothing about it changes, so the effects keyed on it are not re-run by every
  // keystroke in the editor below.
  const demo = useMemo(
    () =>
      sampleId
        ? { sampleId, path, kind, signedIn, changed: stored?.changed === true, greeted: stored?.greeted === true }
        : undefined,
    [sampleId, path, kind, signedIn, stored],
  )

  if (loaded.state === 'loading') {
    return (
      <div className="u-page u-section" style={{ textAlign: 'center' }}>
        <Spinner label={kind === 'sample' ? t('Opening the example', 'جارٍ فتح المثال') : t('Opening your site', 'جارٍ فتح موقعك')} />
      </div>
    )
  }

  const initial = loaded.state === 'ready' ? loaded.data : null

  if (!demo || !initial) {
    return (
      <div className="u-page u-section u-stack">
        {/*
          ⚠️ A MISSING DRAFT IS NOT A MISSING PAGE, AND SAYING SO IS THE WHOLE DIFFERENCE. A site built
          without an account lives in the tab that built it — so a link sent to a friend, or the same
          address opened tomorrow, finds nothing. That is the arrangement working as intended, and the
          honest thing is to say where it went and offer to build another rather than to imply
          something is broken.
        */}
        <Notice tone={kind === 'sample' ? 'error' : 'warn'}>
          {kind === 'sample'
            ? t('That example does not exist.', 'هذا المثال غير موجود.')
            : t(
                'That site was kept in this browser, and it is not here any more — a site nobody claims is cleared after thirty days. Building another is free.',
                'كان ذلك الموقع محفوظًا في هذا المتصفح، ولم يعد موجودًا — يُمسح الموقع الذي لا يحتفظ به أحد بعد ثلاثين يومًا. بناء موقع آخر مجاني.',
              )}
        </Notice>
        <div>
          <Link to="/">
            <Button variant="quiet">
              {kind === 'sample' ? t('Go to the start', 'العودة إلى البداية') : t('Build another', 'ابنِ موقعًا آخر')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return <Workspace id="" initial={initial} handle={sample?.handle ?? initial.handle} published={false} demo={demo} />
}

/**
 * What the editor is told about being a demonstration.
 *
 * ⚠️ ONE OBJECT RATHER THAN FOUR PROPS AND A BOOLEAN, so `demo === undefined` is the whole test for
 * "this is a real site". A `isDemo` flag beside four optional props is four places a branch can be
 * forgotten.
 */
type Demo = {
  /** The key this document is stored under, and the last segment of its URL. */
  sampleId: string
  /**
   * ⚠️ WHERE TO COME BACK TO AFTER SIGNING IN, CARRIED RATHER THAN REBUILT. There are two routes into
   * this workspace now — one of the five examples, and a site the model just wrote for somebody with
   * no account — and `useKeepDemo` leaves the page for an identity provider. Reconstructing the path
   * at that point means a second place that knows which route this was, and the one that gets it
   * wrong sends somebody who has just signed in to a page that says the example does not exist.
   */
  path: string
  /**
   * ⚠️ WHOSE SITE THIS IS, WHICH DECIDES WHAT THE STICKY BAR CALLS IT. One of the five examples is a
   * demonstration and can be described as one; a site the model just wrote from somebody's own
   * sentence is THEIRS, and calling that "an example" reads as the product not having listened.
   */
  kind: 'sample' | 'draft'
  signedIn: boolean
  changed: boolean
  /** Whether the arrival dialog has already been read and closed for this document. */
  greeted: boolean
}

/**
 * Turning a demonstration into a site.
 *
 * ⚠️ IT IS ONE ACT WITH A SIGN-IN IN THE MIDDLE OF IT, AND THE MIDDLE IS WHERE THESE THINGS GET LOST.
 * A signed-out visitor presses Keep; the document is written to the tab, the browser leaves for the
 * identity provider, and comes back to `/demo/<id>?keep=1` — at which point this hook finishes the job
 * it started. Without the resume, somebody signs in and lands back on the same warning banner, having
 * done what it asked.
 *
 * ⚠️ AND IT CREATES THE SITE FROM WHAT IS ON SCREEN, not from the example. Five minutes of edits are
 * the entire reason anybody presses this button.
 */
function useKeepDemo(site: Site, demo: Demo | undefined, leavingToKeep: React.RefObject<boolean>) {
  const navigate = useNavigate()
  const { r } = useLang()
  const [params, setParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  // ⚠️ THE LIVE DOCUMENT, READ AT THE MOMENT OF THE CLICK. A `useCallback` closing over `site` would
  // be re-made on every keystroke and, worse, the resume effect below would fire with whichever
  // document happened to be captured when it mounted.
  const latest = useRef(site)
  latest.current = site

  const keep = useCallback(async () => {
    if (!demo) return
    if (!demo.signedIn) {
      // Write it down before leaving: a crash on the identity provider's side must not cost the edits.
      writeDemo(demo.sampleId, demo.kind, { document: latest.current, changed: true })
      leavingToKeep.current = true
      /*
       * ⚠️ `intent=register`, BECAUSE THIS IS NOW THE MOMENT SOMEBODY REGISTERS. It used to be on the
       * front page's CTA, which no longer asks anyone to sign in — building is free. The person
       * pressing THIS button has built a site they want to keep and, more often than not, has no
       * account yet; the provider is asked for the screen that creates a passkey rather than the one
       * that demands an existing one. See `routes/auth.ts`.
       */
      window.location.href = `/auth/start?intent=register&returnTo=${encodeURIComponent(`${demo.path}?keep=1`)}`
      return
    }

    setBusy(true)
    setFailure(null)
    try {
      const name = r(latest.current.title) || 'My site'
      const created = await api.createSite({ name, document: latest.current })
      /*
       * ⚠️ THE LOCAL COPY IS DROPPED ONLY AFTER THE SERVER HAS ONE. In the other order, a failed
       * create loses the work outright — and this is the one moment somebody has explicitly asked us
       * to look after it.
       */
      clearDemo(demo.sampleId, demo.kind)
      navigate(`/s/${created.id}`, { replace: true })
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
      setBusy(false)
    }
  }, [demo, leavingToKeep, navigate, r])

  /**
   * The other half of the round trip.
   *
   * ⚠️ IT WAITS FOR THE SESSION TO BE KNOWN, AND THE FIRST VERSION DID NOT — SO IT DID NOTHING AT ALL.
   * `signedIn` comes from `GET /api/session`, which is still in flight for the first few hundred
   * milliseconds after the browser comes back from the identity provider; an effect that ran once on
   * arrival saw `false`, cleared the parameter, and left somebody who had just signed in looking at
   * the same warning banner they had pressed the button on. Coming back is not the signal — being
   * signed in is.
   *
   * ⚠️ AND THE PARAMETER IS NOT CLEARED UNTIL SOMETHING IS DONE ABOUT IT. If the sign-in did not take,
   * the intent is still pending and the URL is the only thing carrying it.
   */
  const resumed = useRef(false)
  useEffect(() => {
    if (!demo || resumed.current) return
    if (params.get('keep') !== '1') return
    if (!demo.signedIn) return
    resumed.current = true
    setParams(new URLSearchParams(), { replace: true })
    void keep()
  }, [demo, keep, params, setParams])

  return { keep, busy, failure }
}

/**
 * What a visitor is told when a site that belongs to nobody opens in front of them.
 *
 * > ⚠️ **THIS WAS A STICKY BAR UNDER THE HEADER, AND IT MISBEHAVED THE MOMENT ANYBODY SCROLLED.** It
 * > was `position: sticky` at the header's own height, and the header slides away on a downward
 * > scroll — so the bar sat holding a four-rem gap of nothing above itself, then closed it again on
 * > the way back up. Two sticky elements, one of which moves, cannot be kept in agreement by an
 * > offset that is a constant.
 * >
 * > It also cost every visitor a permanent strip of a 360 px screen to say something that is only
 * > news once. A dialog says it once, properly, and gets out of the way.
 *
 * ⚠️ SO WHAT IT SAYS IS AN INTRODUCTION, NOT A WARNING, AND THE COPY CHANGED WITH THE SHAPE. A
 * banner shouting "NOT SAVED" at somebody who has not typed anything yet is warning them about a
 * loss they cannot have suffered. What is actually useful on arrival is what they are looking at and
 * what they can do with it; the not-saved fact rides along as a plain sentence rather than an alarm.
 *
 * ⚠️ AND DISMISSING IT LOSES NOTHING, BECAUSE IT IS NOT THE ONLY PLACE THE PRODUCT SAYS SO. A
 * dismissable claim would be a dishonest one — somebody closes it, edits for ten minutes and shuts
 * the tab. The header keeps a *Keep this site* button for as long as the site is unkept, which is the
 * same statement in the smallest form that cannot be waved away (see `EditorChrome.keep`).
 */
function LocalSiteDialog({
  kind,
  signedIn,
  busy,
  failure,
  onKeep,
  onClose,
}: {
  kind: 'sample' | 'draft'
  signedIn: boolean
  busy: boolean
  failure: ApiFailure | null
  onKeep: () => void
  onClose: () => void
}) {
  const { t, lang } = useLang()

  const title =
    kind === 'draft'
      ? t('Your website is ready', 'موقعك جاهز')
      : t('This is a real website', 'هذا موقع حقيقي')

  const body =
    kind === 'draft'
      ? t(
          'Everything on it can be changed — tap a heading, a picture, a section, and edit it in place. It is kept in this browser, not in an account, so take your time. Keep it when you like what you see, and it becomes yours.',
          'كل ما فيه قابل للتغيير — انقر عنوانًا أو صورة أو قسمًا وعدّله في مكانه. وهو محفوظ في هذا المتصفح لا في حساب، فخذ وقتك. احتفظ به عندما يعجبك ليصبح لك.',
        )
      : t(
          'It was built with the editor you are about to use, and every part of it can be changed. Nothing you do here is saved, so try anything — then describe your own business and get one like it.',
          'بُني بالمحرّر الذي ستستخدمه، وكل جزء فيه قابل للتغيير. لا يُحفظ شيء ممّا تفعله هنا، فجرّب ما شئت — ثم صِف نشاطك واحصل على موقع مثله.',
        )

  return (
    <Dialog labelledBy="ed-hello-title" onClose={onClose}>
      <h2 className="u-title" id="ed-hello-title">
        {title}
      </h2>
      <p className="u-lead">{body}</p>

      {failure && <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>}

      <div className="ed__hello__actions">
        {/*
          ⚠️ LOOKING AROUND IS THE PRIMARY ACTION, AND PUTTING "KEEP" SECOND IS A DELIBERATE REFUSAL
          TO UPSELL. §5.7: what is not copied from the inspiration is the ask-at-every-step pattern.
          Somebody who has been looking at this site for four seconds cannot yet want to keep it,
          and a product that asks anyway teaches people to dismiss it without reading.
        */}
        <Button onClick={onClose}>{t('Look around', 'تصفّح الموقع')}</Button>

        {kind === 'draft' ? (
          <Button variant="quiet" busy={busy} onClick={onKeep}>
            {signedIn ? t('Keep this site', 'احتفظ بهذا الموقع') : t('Sign in and keep it', 'سجّل الدخول واحتفظ به')}
          </Button>
        ) : (
          /* Nobody wants to keep the flower shop. What they might want, having seen it work, is
             one of their own — which is the front page, where they came from. */
          <Link to="/" className="u-btn u-btn--quiet">
            {t('Build my own', 'ابنِ موقعي')}
          </Link>
        )}
      </div>

      <p className="u-tiny u-faint">
        {t(
          'Building is free, and so is publishing for the first week.',
          'البناء مجاني، والنشر مجاني في الأسبوع الأول.',
        )}
      </p>
    </Dialog>
  )
}

function Workspace({
  id,
  initial,
  handle,
  published,
  demo,
}: {
  id: string
  initial: Site
  handle: string | null
  /** Whether the live snapshot was up to date with this document when the site was opened. */
  published: boolean
  /** Present only for one of the landing page's examples — see `DemoEditor`. */
  demo?: Demo
}) {
  const { t, lang, r } = useLang()

  // ⚠️ THE SITE'S OWN NAME, because this is the one screen somebody has four of open at once. A tab
  // strip of identical titles is a tab strip you cannot navigate.
  const siteName = r(initial.title)
  useTitle(siteName || 'Editing', siteName || 'التحرير')

  const [site, setSite] = useState<Site>(initial)
  const [pageId, setPageId] = useState(initial.pages[0]?.id ?? '')
  const [selected, setSelected] = useState<BlockRef | null>(null)
  const [pane, setPane] = useState<Pane>('stage')

  /**
   * ⚠️ SELECTED AND OPEN ARE TWO STATES, NOT ONE, AND THAT IS THE WHOLE INTERACTION. One tap on a
   * block selects it — the drawer's handle names it and offers up/down, and the page stays fully
   * visible. A second tap on the SAME block opens the fields. Moving a section is the commonest
   * thing anybody does in a builder, and it now costs one tap with nothing covering the page;
   * opening a form over the preview is the rarer, heavier act and asks for the second tap.
   */
  const [drawerOpen, setDrawerOpen] = useState(false)

  /**
   * Whether the drawer is showing the colour schemes rather than a block.
   *
   * ⚠️ A MODE OF THE DRAWER, NOT A SIXTH PANEL SOMEWHERE. Choosing a look is exactly like editing a
   * heading: you do it while watching the page change, and the drawer is the surface this editor uses
   * for "change something and see it". It is also why it does not disturb the selection — flipping
   * through five palettes and then going back to the paragraph you were writing should not cost a
   * second tap.
   */
  const [theme, setTheme] = useState(false)

  /**
   * Which single field the drawer is showing, if it is showing one.
   *
   * ⚠️ A FIELD IS A THIRD STATE, NOT A FILTER ON THE SECOND. Tapping the heading in the preview opens
   * the drawer on the heading and NOTHING else — not the whole form scrolled to it. On a phone the
   * difference is a sheet with one input in it against a sheet you have to search, and the customer
   * has already said which one they meant by pointing at it.
   */
  const [field, setField] = useState<string | null>(null)

  /**
   * Which ROW of a repeater the drawer is showing, when it is showing one.
   *
   * ⚠️ A ROW IS A FOURTH STATE FOR THE SAME REASON A FIELD WAS A THIRD. Somebody who tapped Friday's
   * opening hours meant Friday's, and a sheet holding the whole list scrolled to it is a sheet you
   * have to search on a phone. It is an index into the document's array — never into what is on
   * screen, which is filtered (see `listTargets.ts`).
   */
  const [row, setRow] = useState<number | null>(null)
  /**
   * ⚠️ `local` IS A FIFTH STATE AND IT IS NOT A SYNONYM FOR `clean`. It means "written down in this
   * browser and nowhere else", which is what a demonstration's edits are. Reusing `clean` would have
   * the header say *Saved* about work that is on one machine, belongs to nobody, and is swept in
   * thirty days — the exact lie §0.4 exists to prevent, told by the one component that knows better.
   */
  const [saving, setSaving] = useState<SaveState>(demo?.changed ? 'local' : 'clean')
  const [full, setFull] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadFailure, setUploadFailure] = useState<ApiFailure | null>(null)

  /**
   * ⚠️ UNDO IS A STACK OF WHOLE DOCUMENTS, WHICH IS ONLY AFFORDABLE BECAUSE THE OPS ARE IMMUTABLE.
   * Each entry shares structure with the one before it — `setProp` copies one block and reuses every
   * other object — so fifty steps of history is a few kilobytes rather than fifty copies of the
   * site. Capped anyway, because a long editing session should not grow without bound.
   */
  const history = useRef<Site[]>([])

  /**
   * Whether what a visitor would see is what is on this screen.
   *
   * ⚠️ IT IS NOT `saving === 'clean'`, AND CONFLATING THE TWO IS THE BUG THIS EXISTS TO AVOID. Saved
   * means the DOCUMENT is on the server; published means an immutable snapshot was rendered from it
   * (§2.2). An edit that saves in half a second leaves the site saved and the snapshot a version
   * behind for as long as the customer likes, so the header would go back to offering «Open» — a
   * link to the phone number they just corrected.
   */
  const [snapshotCurrent, setSnapshotCurrent] = useState(published)

  /** Whether the publish wizard is open. Opened from three places — see `openPublish` on the chrome. */
  const [publishing, setPublishing] = useState(false)

  /**
   * The claimed address, which the editor loads once and the wizard can change.
   *
   * ⚠️ IT IS STATE RATHER THAN THE PROP, AND WITHOUT THAT THE HEADER IS WRONG ON THE FIRST PUBLISH.
   * A draft has no address; the customer claims one inside the wizard and publishes in the same
   * breath — so the prop is still `null` at the moment the header has to decide whether to offer
   * «Open», and it stays `null` until a reload. Found by the browser suite, which is the only place
   * a stale prop like this shows up.
   */
  const [address, setAddress] = useState(handle)

  const apply = useCallback((next: Site) => {
    setSite((current) => {
      history.current = [...history.current.slice(-49), current]
      return next
    })
    setSaving('dirty')
    setSnapshotCurrent(false)
  }, [])

  /**
   * ⚠️ THE SAME EDIT AS `apply`, EXPRESSED AGAINST WHATEVER THE DOCUMENT IS WHEN IT LANDS. A bulk
   * translation is in flight for several seconds and the customer keeps typing; `apply(next)` built
   * from the site as it was when the request went out would throw those keystrokes away. The gap
   * addresses survive that (see `gaps.ts`) precisely so the fold can be applied late.
   */
  const applyFold = useCallback((fold: (current: Site) => Site) => {
    setSite((current) => {
      history.current = [...history.current.slice(-49), current]
      return fold(current)
    })
    setSaving('dirty')
    setSnapshotCurrent(false)
  }, [])

  const undo = useCallback(() => {
    const previous = history.current.pop()
    if (!previous) return
    setSnapshotCurrent(false)
    setSite(previous)
    setSaving('dirty')
  }, [])

  /**
   * Autosave.
   *
   * ⚠️ DEBOUNCED, AND THE STATE IS SHOWN. A customer typing a headline should not fire a request per
   * keystroke, and a customer who closes the tab should be able to tell whether their last change
   * landed. "Saved" that is a lie is worse than no indicator at all — which is why the failure state
   * exists and is loud.
   */
  useEffect(() => {
    if (saving !== 'dirty') return

    /*
     * ⚠️ A DEMONSTRATION IS WRITTEN TO THE TAB, NOT TO THE SERVER, AND IT IS STILL DEBOUNCED. There is
     * no request to spare — but there IS a whole document to serialise, and doing that per keystroke
     * on a phone is the same jank for none of the benefit. What must not be debounced is the WARNING:
     * `saving` left `clean` the moment the customer changed something, so the sticky bar is already up
     * while this timer is still counting.
     */
    if (demo) {
      const timer = setTimeout(() => {
        writeDemo(demo.sampleId, demo.kind, { document: site, changed: true })
        setSaving((s) => (s === 'dirty' ? 'local' : s))
      }, 400)
      return () => clearTimeout(timer)
    }

    const timer = setTimeout(() => {
      setSaving('saving')
      api.saveDocument(id, site).then(
        () => setSaving((s) => (s === 'saving' ? 'clean' : s)),
        () => setSaving('failed'),
      )
    }, 900)
    return () => clearTimeout(timer)
  }, [site, saving, id, demo])

  // Escape leaves full screen, because that is what Escape does everywhere else and a customer
  // should not have to find the button again.
  useEffect(() => {
    if (!full) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])

  // ⚠️ THE BROWSER'S OWN WARNING, FOR THE ONE SECOND OF UNSAVED WORK THE DEBOUNCE CREATES. Without
  // it, closing the tab mid-sentence silently loses the sentence.
  //
  // ⚠️ AND IT HOLDS OFF THE DEPLOY WATCHER FOR THE SAME WINDOW. A reload to pick up a new build is
  // worth doing; doing it over a half-typed headline is not. `beforeunload` cannot stop a reload we
  // trigger ourselves, so the hold is explicit.
  /**
   * ⚠️ AND IT IS SILENCED FOR THE ONE NAVIGATION THAT EXISTS TO *SAVE* THE WORK. Keeping a
   * demonstration means leaving for an identity provider, so the browser would ask "leave site?" at
   * the exact moment somebody pressed the button that keeps their site. The ref is set immediately
   * before that assignment and never reset, because the page is on its way out.
   */
  const leavingToKeep = useRef(false)

  useEffect(() => {
    if (saving === 'clean') return
    const release = holdReload()
    const warn = (e: BeforeUnloadEvent) => {
      if (leavingToKeep.current) return
      e.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => {
      release()
      window.removeEventListener('beforeunload', warn)
    }
  }, [saving])

  /**
   * ⚠️ THE SECOND TAP IS ON THE SAME BLOCK, NOT ANY BLOCK. Tapping a different section while the
   * drawer is open moves the drawer to it and does NOT close it — somebody working through a page
   * would otherwise have to re-open the fields for every section. What a repeat tap means is "I
   * meant this one", and that is the only thing that opens.
   */
  /**
   * ⚠️ TAPPING THE PAGE CLOSES THE DRAWER, ALWAYS. That is the gesture that means "let me see my
   * site" — and with the drawer taking half the screen it is the one somebody reaches for most. It
   * used to OPEN on a second tap, which meant the way to get the page back was to find a control.
   */
  const select = useCallback((ref: BlockRef) => {
    setSelected(ref)
    setField(null)
    setRow(null)
    setTheme(false)
    setDrawerOpen(false)
  }, [])

  /**
   * Tapping a heading, a line of copy, a picture — or one row of a repeater: the drawer opens on that
   * one thing.
   */
  const selectField = useCallback((ref: BlockRef, key: string, index?: number) => {
    setSelected(ref)
    setField(key)
    setRow(index ?? null)
    setTheme(false)
    setDrawerOpen(true)
  }, [])

  /** The whole form, from the "Edit" tab on the block. */
  const openBlock = useCallback((ref: BlockRef) => {
    setSelected(ref)
    setField(null)
    setRow(null)
    setTheme(false)
    setDrawerOpen(true)
  }, [])

  /**
   * Arriving from a Publish button somewhere else.
   *
   * ⚠️ A QUERY PARAMETER RATHER THAN A HASH, because what it opens is not an anchor on a page — it
   * was a group inside a pane that might not be rendered yet, and it is a dialog now. The parameter
   * is cleared once it has been acted on, so a reload does not drag somebody back into a ceremony
   * they navigated away from.
   */
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    if (!id || params.get('publish') !== '1') return
    setPublishing(true)
    setParams((next) => {
      next.delete('publish')
      return next
    }, { replace: true })
  }, [id, params, setParams])

  const page = site.pages.find((p) => p.id === pageId) ?? site.pages[0]
  const block = selected ? getBlock(site, selected) : undefined
  const def = block ? getBlockDef(block.type) : undefined

  /**
   * Choosing a picture.
   *
   * ⚠️ A FILE INPUT CREATED ON DEMAND AND THROWN AWAY, rather than a hidden `<input>` living in the
   * tree. One hidden input shared by every image slot has to be re-pointed at whichever field asked,
   * and the bug that produces — a photograph landing in the slot you clicked a moment ago — is
   * invisible until somebody uploads two pictures quickly.
   *
   * ⚠️ AND THE ORIENTATION COMES FROM THE SLOT, so the browser resizes to what the block will
   * actually draw. See `prepareImage`.
   */
  const pickImage = useCallback(
    (current: ImageRef, set: (next: ImageRef) => void) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/jpeg,image/png,image/webp'
      input.onchange = async () => {
        const file = input.files?.[0]
        if (!file) return
        /*
         * ⚠️ AN UPLOAD NEEDS A SITE TO BELONG TO, AND A DEMONSTRATION HAS NONE. The media route keys
         * objects under `media/<siteId>/…`; there is no id here because nothing has been created.
         * Saying so beside the button is the honest failure — the alternative is a request that 404s
         * and a customer who concludes the uploader is broken.
         */
        if (!id) {
          setUploadFailure(
            new ApiFailure(
              'demo.notSaved',
              {
                en: 'Keep this site first, then you can add your own pictures.',
                ar: 'احفظ هذا الموقع أولًا، ثم يمكنك إضافة صورك.',
              },
              0,
            ),
          )
          return
        }
        setUploading(true)
        setUploadFailure(null)
        try {
          const prepared = await prepareImage(file, current.orientation)
          const { key } = await api.uploadMedia(id, prepared)
          set({
            status: 'ready',
            key,
            // ⚠️ ALT TEXT STARTS EMPTY AND IS NOT INVENTED FROM THE FILENAME. `IMG_4821.jpg` read
            // aloud by a screen reader is worse than silence, and an empty alt is a MEANINGFUL value:
            // it marks the picture decorative until somebody describes it.
            alt: { en: '', ar: '' },
            orientation: current.orientation,
            source: 'upload',
          })
        } catch (error) {
          setUploadFailure(error instanceof ApiFailure ? error : null)
        } finally {
          setUploading(false)
        }
      }
      input.click()
    },
    [id],
  )

  /**
   * Adding a row from the preview.
   *
   * ⚠️ IT OPENS THE ROW IT JUST ADDED. A `+` that appends an invisible empty row to a list and leaves
   * the customer looking at an unchanged page has done nothing they can see — every block filters
   * blank rows out of the render, so the new one is not even on the screen. The act is "add another
   * one of these AND let me fill it in", and it is one press.
   */
  const addRow = useCallback(
    (ref: BlockRef, key: string) => {
      const block = getBlock(site, ref)
      const def = block ? getBlockDef(block.type) : undefined
      const spec = def?.fields.find((f) => f.key === key)
      if (!block || !spec || spec.type.kind !== 'list') return
      const rows = Array.isArray(block.props[key]) ? (block.props[key] as Record<string, unknown>[]) : []
      if (spec.type.max !== undefined && rows.length >= spec.type.max) return
      apply(setProp(site, ref, key, [...rows, defaultRow(spec.type.item)]))
      selectField(ref, key, rows.length)
    },
    [apply, selectField, site],
  )

  const keeping = useKeepDemo(site, demo, leavingToKeep)

  /**
   * Whether the arrival dialog is up.
   *
   * ⚠️ ONCE PER DOCUMENT, AND REMEMBERED WHERE THE DOCUMENT IS. A dialog that returns on every reload
   * is a dialog people learn to close without reading — and this tab reloads itself when a new build
   * ships (`useDeployWatch`). It is also not shown at all to somebody who has already edited: they
   * arrived here before, were told, and came back.
   */
  const [greeting, setGreeting] = useState(() => Boolean(demo) && !demo?.changed && !demo?.greeted)

  const dismissGreeting = useCallback(() => {
    setGreeting(false)
    if (demo) writeDemo(demo.sampleId, demo.kind, { document: site, changed: saving !== 'clean', greeted: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, site, saving])

  usePublishEditorChrome(
    useMemo(
      () => ({
        pane,
        setPane,
        full,
        setFull,
        undo,
        canUndo: history.current.length > 0,
        saving,
        template: site.template,
        setTemplate: (id: string) => apply(setTemplate(site, id)),
        keep: demo ? { busy: keeping.busy, onKeep: () => void keeping.keep() } : undefined,
        // ⚠️ THE SAME SLOT AS `keep`, AND THE TWO CANNOT BOTH BE TRUE. No id means the site lives in
        // the browser and cannot be published; an id means it has already been kept.
        publishable: Boolean(id),
        openPublish: () => setPublishing(true),
        // ⚠️ AND THE ADDRESS ONLY WHILE THE SNAPSHOT IS THIS DOCUMENT. `handle` alone is not enough:
        // a site keeps its address after it is published and goes on being edited, so a header keyed
        // on the handle would offer «Open» over a page a visitor cannot see yet.
        liveUrl: id && address && snapshotCurrent ? publishedUrl(address) : undefined,
        theme,
        setTheme: (open: boolean) => {
          setTheme(open)
          setDrawerOpen(open)
          // The preview is the point of opening this, so a customer who was on the rail is taken to it.
          if (open) setPane('stage')
        },
      }),
      // ⚠️ `history.current.length` IS READ THROUGH `saving`, WHICH IS THE HONEST DEPENDENCY. A ref
      // does not trigger a render, so a memo keyed on it would go stale; `saving` changes on every
      // edit, which is exactly when the undo stack changes.
      [pane, full, undo, saving, site, apply, theme, demo, id, address, snapshotCurrent, keeping.busy, keeping.keep],
    ),
  )

  const fieldContext: FieldContext = useMemo(
    () => ({
      pages: site.pages,
      onPickImage: pickImage,
      // The same route the preview loads pictures from, a few lines down — one origin for an object
      // key, so a thumbnail cannot show something different from the page behind the drawer.
      mediaUrl: (key: string) => `/api/media/${encodeURIComponent(key)}`,
      // ⚠️ THE SITE'S NAME GOES WITH IT so a brand is not translated into a description of itself.
      // «مقهى العقيق» is a business called Aqiq Cafe, not "the agate coffee shop".
      onTranslate: (args) => api.translate({ ...args, siteName }),
    }),
    [site.pages, pickImage, siteName],
  )

  const labelFor = useCallback(
    (type: string) => {
      const def = getBlockDef(type)
      return def ? r(def.label) : type
    },
    [r],
  )

  if (!page) return null

  return (
    <>
      {/*
        > ⚠️ **TWO FULL-WIDTH BARS STOOD HERE — `ed__bar` AND `ed__switch` — AND THEY ARE GONE.**
        > Between them they carried the back link, the address, the save state, undo, the preview
        > language and the pane switcher, in two stacked rows under the application header. On a
        > 360 px phone that was three bars and about a third of the screen before any of the
        > customer's site appeared. Everything they held is in one menu in the header now; see
        > `editorChrome.tsx` for how the state gets there.
      */}
      {demo && greeting && (
        <LocalSiteDialog
          kind={demo.kind}
          signedIn={demo.signedIn}
          busy={keeping.busy}
          failure={keeping.failure}
          onKeep={() => void keeping.keep()}
          onClose={dismissGreeting}
        />
      )}

      {/*
        ⚠️ THE WIZARD IS MOUNTED BY THE WORKSPACE, NOT BY THE RAIL, because it is a dialog over the
        whole editor and because it must survive a pane switch. It is unmounted rather than hidden
        when it closes, so its checks are fetched fresh each time it opens — a checklist that was
        loaded ten minutes and thirty edits ago is a checklist about a different site.
      */}
      {publishing && id && (
        <PublishWizard
          id={id}
          handle={address}
          onAddress={setAddress}
          dirty={saving !== 'clean'}
          onPublished={() => setSnapshotCurrent(true)}
          onClose={() => setPublishing(false)}
          onGoTo={(where) => {
            if (where.pageId) setPageId(where.pageId)
            if (where.blockId) {
              openBlock({ scope: 'page', pageId: where.pageId ?? page.id, blockId: where.blockId })
              setPane('stage')
            }
          }}
        />
      )}

      <div className="ed">
        {/* ---------------------------------------------------------------- pages + palette --- */}
        <aside className={`ed__rail ed__pane ${pane === 'rail' ? 'ed__pane--active' : ''}`}>
          <div className="ed__group">
            <p className="ed__label">{t('Pages', 'الصفحات')}</p>
            <div className="u-stack u-stack--tight">
              {/*
                ⚠️ THE ROW ENDS IN «OPEN», AND ON A PHONE THAT IS THE ONLY THING ON IT THAT GOES
                ANYWHERE. Choosing a page in this list sets which page is being edited and leaves the
                customer looking at the list — which is correct on a desktop, where the preview is
                the next column along, and is a dead end on a phone, where the rail IS the screen and
                the page they just chose is behind a menu labelled «Preview». Same act, two panes:
                the name selects, «Open» selects and shows.

                ⚠️ AND THE ORDERING WENT INTO A MENU, WHICH IS THE SAME MOVE §3.3 MADE FOR A
                REPEATER'S ROW. It was a bare ↑ with no ↓ — half an operation, in an unlabelled icon,
                competing for a 17rem rail with the button that actually opens the page. The menu's
                items have names; the arrow did not, and there was no way to move a page down at all.
              */}
              {site.pages.map((p, i) => (
                <div key={p.id} className="ed__pagerow">
                  <button
                    className={`ed__item ${p.id === page.id ? 'ed__item--active' : ''}`}
                    onClick={() => {
                      setPageId(p.id)
                      setSelected(null)
                    }}
                  >
                    {r(p.title)}
                  </button>
                  <Menu
                    label={t(`Reorder ${r(p.title)}`, `إعادة ترتيب ${r(p.title)}`)}
                    actions={[
                      {
                        label: t('Move up', 'لأعلى'),
                        disabled: i === 0,
                        onClick: () => apply(movePage(site, p.id, -1)),
                      },
                      {
                        label: t('Move down', 'لأسفل'),
                        disabled: i === site.pages.length - 1,
                        onClick: () => apply(movePage(site, p.id, 1)),
                      },
                    ]}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPageId(p.id)
                      setSelected(null)
                      setPane('stage')
                    }}
                  >
                    {t('Open', 'افتح')}
                  </Button>
                </div>
              ))}
            </div>
            <div className="u-row" style={{ marginBlockStart: 'var(--u-s3)' }}>
              <Button
                variant="quiet"
                size="sm"
                onClick={() => {
                  const added = addPage(site)
                  apply(added.site)
                  setPageId(added.pageId)
                }}
              >
                {t('Add page', 'أضف صفحة')}
              </Button>
            </div>
          </div>

          <div className="ed__group">
            <p className="ed__label">{t('This page', 'هذه الصفحة')}</p>
            <PageSettings site={site} pageId={page.id} onChange={apply} />
          </div>

          <div className="ed__group">
            <p className="ed__label">{t('Add a section', 'أضف قسمًا')}</p>
            <div className="ed__palette">
              {pageBlockDefs().map((d) => (
                <button
                  key={d.type}
                  onClick={() => {
                    const added = addBlock(site, page.id, d.type)
                    if (!added) return
                    apply(added.site)
                    // A section you just added is one you are about to fill in, so this is the one
                    // path that opens the whole form without being asked.
                    openBlock(added.ref)
                    setPane('stage')
                  }}
                >
                  {r(d.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="ed__group">
            <p className="ed__label">{t('The whole site', 'الموقع كله')}</p>
            <SiteSettings site={site} onChange={apply} />
          </div>

          {/*
            ⚠️ THE ID IS A DESTINATION, NOT DECORATION. Publishing is the one thing this product asks
            to be paid for, and on a phone it was the last group of a pane you reach through a menu
            labelled «Preview» — two taps and a scroll, with nothing anywhere naming it. The header's
            menu now has a «Publish» item that comes here; see `EditorMenu` in `App.tsx`.
          */}
          <div className="ed__group" id="ed-publish">
            {/*
              ⚠️ NO PUBLISH PANEL FOR A DEMONSTRATION, AND NOT BECAUSE IT WOULD LOOK WRONG. That panel
              asks the server for `/sites/:id/checks`, and there is no id — so it would spend a request
              on a 404 and then show a signed-out visitor an error about a site that was never created.
              What replaces it is the one thing that IS available here: keeping it.
            */}
            {demo ? (
              <div className="u-stack u-stack--tight">
                <p className="ed__label">{t('Publishing', 'النشر')}</p>
                <p className="u-small u-faint">
                  {demo.kind === 'sample'
                    ? t(
                        'This example lives in this browser tab. Keep it to get an address, and to publish it.',
                        'هذا المثال موجود في هذه النافذة فقط. احتفظ به للحصول على عنوان ولنشره.',
                      )
                    : t(
                        'This site is kept in this browser for thirty days. Keep it to get an address, and to publish it.',
                        'هذا الموقع محفوظ في هذا المتصفح لمدة ثلاثين يومًا. احتفظ به للحصول على عنوان ولنشره.',
                      )}
                </p>
                <div className="u-row">
                  <Button size="sm" busy={keeping.busy} onClick={() => void keeping.keep()}>
                    {demo.signedIn ? t('Keep this site', 'احتفظ بهذا الموقع') : t('Sign in and keep it', 'سجّل الدخول واحتفظ به')}
                  </Button>
                </div>
              </div>
            ) : (
              /*
                ⚠️ THE RAIL NOW HOLDS A DOOR RATHER THAN THE WHOLE CEREMONY. The checklist, the
                address and the button moved into `PublishWizard`, which is a dialog — see the note
                at the top of that file for why a 17rem column was the wrong shape for the one act
                this product charges for. What is left here is the group the header's menu and the
                sites list already navigate to, so every route in still lands somewhere.
              */
              <div className="u-stack u-stack--tight">
                <p className="ed__label">{t('Publishing', 'النشر')}</p>
                <p className="u-small u-faint">
                  {snapshotCurrent
                    ? t('This site is on the internet, and this is what visitors see.', 'هذا الموقع منشور، وهذا ما يراه الزوار.')
                    : t(
                        'Nothing is on the internet until you publish. It is free for the first week.',
                        'لا شيء منشور حتى تنشره. وهو مجاني في الأسبوع الأول.',
                      )}
                </p>
                <div className="u-row">
                  <Button size="sm" onClick={() => setPublishing(true)}>
                    {t('Publish', 'انشر')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* ------------------------------------------------------------------------ preview --- */}
        {/*
          ⚠️ `--drawer` IS SCROLL ROOM UNDER THE PAGE, AND WITHOUT IT THE LAST SECTION OF A SITE
          CANNOT BE EDITED AT ALL. The drawer is `position: fixed` over the bottom half; the preview
          scrolls behind it and stops when the page ends — so anything in the last half-screen can be
          seen only until you tap it, at which point the sheet covers the thing you tapped and there
          is nothing left to scroll. The footer and the last block of every site were in that band.
        */}
        <div
          className={`ed__stage ed__pane ${pane === 'stage' ? 'ed__pane--active' : ''} ${full ? 'ed__stage--full' : ''} ${
            drawerOpen ? 'ed__stage--drawer' : ''
          }`}
        >
          {/*
            ⚠️ FULL SCREEN IS THE SAME PREVIEW, NOT A SECOND ONE. It widens the pane and drops the
            editor chrome around it; the tree inside is untouched, so a block is still clickable and
            still selects. A separate "preview mode" that rendered the site again would be a second
            renderer with its own bugs, which is what §2.2 exists to prevent.
            ⚠️ AND IT IS NOT THE FULLSCREEN API. `requestFullscreen` takes over the whole display,
            hides the browser's own chrome, and on iOS Safari does not work on a div at all. This is a
            CSS state, so Escape, the back button and a rotated phone all behave normally.
          */}
          {/* The full-screen toggle moved into the header menu; see `EditorMenu`. */}
          {/*
            ⚠️ ABOVE THE PREVIEW AND NOT IN THE RAIL, because what it is about is the page: on a phone
            the rail is a pane you have to go to, and a language that has not been written yet is a
            thing you notice by looking at the site. It renders nothing at all when there is nothing
            missing (CLAUDE.md §0.4 — the claim belongs to whatever knows, and disappears with it).
          */}
          <LanguageFill
            site={site}
            lang={lang}
            /*
             * ⚠️ ONLY A DOCUMENT THAT LIVES IN THE BROWSER FILLS ITSELF IN. Those blanks are ones the
             * generator was told not to write (§5.15) — finishing our own job. On a site with an
             * owner a blank may be a decision, so it is offered and nothing is spent unasked.
             */
            auto={Boolean(demo)}
            siteName={siteName}
            onApply={applyFold}
          />
          <div className="ed__frame">
            {/*
              The direction of the PREVIEW follows the previewed language, independently of the
              application's own direction — otherwise an English-speaking customer previewing their
              Arabic page would see it laid out left-to-right, which is not what publishes.
            */}
            <div dir={lang === 'ar' ? 'rtl' : 'ltr'} lang={lang}>
              <SelectionLayer
                selected={selected}
                onSelect={select}
                onSelectField={selectField}
                onAddRow={addRow}
                onOpenBlock={openBlock}
                onMove={(ref, by) => apply(moveBlock(site, ref.pageId, ref.blockId, by))}
                site={site}
                pageId={page.id}
                blocks={page.blocks}
                labelFor={labelFor}
              >
                <SitePage
                  site={site}
                  page={page}
                  lang={lang}
                  mode="preview"
                  // In preview an image key is served through the API, behind the session. On a
                  // published page it is a public object on the customer's own origin — which is why
                  // the renderer takes this as a parameter rather than deciding for itself.
                  mediaUrl={(key) => `/api/media/${encodeURIComponent(key)}`}
                />
              </SelectionLayer>
            </div>
          </div>
        </div>

      </div>

      {/* ------------------------------------------------------------------------- drawer --- */}
      <BlockDrawer
        site={site}
        selected={selected}
        open={drawerOpen}
        field={field}
        row={row}
        onRow={setRow}
        theme={theme}
        onMinimise={() => {
          setDrawerOpen(false)
          setField(null)
          setRow(null)
          setTheme(false)
        }}
        onApply={apply}
        onSelect={(ref) => {
          setSelected(ref)
          setField(null)
          setRow(null)
          setDrawerOpen(Boolean(ref))
        }}
        ctx={fieldContext}
        uploading={uploading}
        uploadFailure={uploadFailure}
      />
    </>
  )
}

/** Whether two selections point at the same thing. */
function sameRef(a: BlockRef, b: BlockRef): boolean {
  if (a.scope !== b.scope) return false
  if (a.scope !== 'page' || b.scope !== 'page') return true
  return a.pageId === b.pageId && a.blockId === b.blockId
}

/**
 * The fields, as a sheet at the bottom of the page.
 *
 * > ⚠️ **A THIRD COLUMN CALLED `ed__inspector` STOOD HERE, AND ON A PHONE IT WAS A WHOLE SCREEN.**
 * > Editing a heading meant leaving the preview, changing the text, and coming back to see what it
 * > did. A builder whose entire proposition is *showing you the page* cannot take the page away at
 * > the exact moment you change it.
 *
 * > ⚠️ **AND THE MINIMISED STATE USED TO CARRY CONTROLS — UP, DOWN, THE BLOCK'S NAME, AN EDIT
 * > BUTTON — WHICH IS NOT WHAT A DRAWER IS.** It was a toolbar that happened to expand. Ordering
 * > moved onto the block itself, where the thing being ordered is (see `SelectionLayer`), and what
 * > is left when the drawer is shut is a handle: a grabber, 2.5 rem, nothing to read and nothing to
 * > mis-tap. A closed drawer should cost the page almost nothing, and now it costs 40 px.
 *
 * ⚠️ THREE STATES, NOT TWO. Shut; open on ONE field, because the customer pointed at a heading and
 * meant the heading; open on the whole block, from the Edit tab. The middle one is the common case
 * and it is why a filtered "scroll to the field" would not do — on a phone that is a sheet you have
 * to search versus a sheet with one input in it.
 */
function BlockDrawer({
  site,
  selected,
  open,
  field,
  row,
  onRow,
  theme,
  onMinimise,
  onApply,
  onSelect,
  ctx,
  uploading,
  uploadFailure,
}: {
  site: Site
  selected: BlockRef | null
  open: boolean
  field: string | null
  /** Which row of `field`, when `field` is a repeater and the customer pointed at one row of it. */
  row: number | null
  onRow: (row: number | null) => void
  /** The drawer is showing the colour schemes instead of a block. */
  theme: boolean
  onMinimise: () => void
  onApply: (site: Site) => void
  onSelect: (ref: BlockRef | null) => void
  ctx: FieldContext
  uploading: boolean
  uploadFailure: ApiFailure | null
}) {
  const { t, lang, r } = useLang()

  const block = selected ? getBlock(site, selected) : undefined
  const def = block ? getBlockDef(block.type) : undefined

  /**
   * ⚠️ OPENING THE DRAWER SCROLLS THE BLOCK CLEAR OF IT. Half the viewport is a sheet the moment it
   * opens, and the section being edited is as likely to be under that half as above it — which would
   * reproduce, at the bottom of the screen, the very thing the third column did wrong.
   *
   * ⚠️ IT AIMS AT THE FIELD WHEN THERE IS ONE. Centring the whole block puts a tall hero's heading
   * off the top; the customer tapped a specific line and that line is what has to stay visible.
   */
  useEffect(() => {
    if (!open || !selected) return
    const scope = selected.scope
    const blockEl =
      scope === 'page'
        ? document.getElementById(selected.blockId)
        : document.querySelector(`.ed__frame ${scope === 'navbar' ? 'nav' : 'footer'}`)
    if (!blockEl) return
    /*
     * ⚠️ THE ROW, NOT THE LIST, WHEN THERE IS A ROW. Centring the opening-hours table puts the row
     * being edited anywhere in it; the customer pointed at one line and that line is what has to stay
     * on the screen.
     */
    const selector =
      field && row !== null
        ? `[data-ed-field="${CSS.escape(field)}"][data-ed-row="${row}"]`
        : field
          ? `[data-ed-field="${CSS.escape(field)}"]`
          : ''
    const target = (selector && blockEl.querySelector(selector)) || blockEl
    target.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [open, selected, field, row])

  const onPage = selected?.scope === 'page'
  const openedField = def && field ? def.fields.find((spec) => spec.key === field) : undefined
  /**
   * ⚠️ A FIELD MAY BRING ONE OTHER WITH IT, AND THE REGISTRY SAYS WHICH — see `FieldSpec.alongside`.
   * Tapping a button in the preview matches its WORDS, so the drawer opened on `ctaLabel` and the
   * destination was nowhere: "I cannot change where it goes". They are two fields to us and one
   * object to the customer.
   *
   * ⚠️ AND IT IS A FILTER RATHER THAN A CONCATENATION, so the pair keeps the registry's own order —
   * the words before where they go, on every block, without either block saying so.
   */
  const shownFields = def
    ? field
      ? def.fields.filter((spec) => spec.key === field || spec.key === openedField?.alongside)
      : def.fields
    : []
  /**
   * ⚠️ THE ROW IS ONLY HONOURED IF IT STILL EXISTS. A row can go away while the drawer is open on it —
   * the customer removed it, or an undo took the list back to before it was added — and rendering
   * `rows[7]` of a five-row list is a blank sheet with no way out. Falling back to the whole field is
   * the state the customer would have got by tapping the list instead.
   */
  const rowSpec = row !== null ? openedField : undefined
  const rowValue = rowSpec && block ? block.props[rowSpec.key] : undefined
  const showRow =
    rowSpec?.type.kind === 'list' && Array.isArray(rowValue) && row !== null && row < (rowValue as unknown[]).length

  /**
   * > ⚠️ **THIS SAID THE HANDLE IS ALWAYS RENDERED, EVEN WITH NOTHING SELECTED**, on two arguments: "a
   * > drawer that appears and vanishes as the customer taps around is a layout that jumps", and the
   * > grabber is what says there is a drawer here at all.
   * >
   * > The first was simply wrong — the drawer is `position: fixed`, so it is not in the layout and
   * > nothing jumps when it goes. The second bought an affordance at the price of a lie: with nothing
   * > selected the handle's only action is `selected && …`, so it is a pill at the bottom of the
   * > screen that looks exactly like a control and does nothing at all when pressed. A dead control is
   * > worse than an absent one, because pressing it teaches the customer that the editor is broken.
   *
   * There is nothing to open until something is selected, so there is nothing to show. Tapping any
   * section brings it back, which is the gesture the whole editor is built on.
   */
  if (!selected && !theme) return null

  return (
    <div className={`ed__drawer ${open ? 'ed__drawer--open' : ''}`}>
      {/*
        ⚠️ THE WHOLE HANDLE IS THE BUTTON, NOT A GRABBER WITH A HIT AREA AROUND IT. It is 40 px of
        full-width target for the one gesture that gets the page back, which is what a thumb reaching
        the bottom of a phone can actually hit.
      */}
      <button
        type="button"
        className="ed__handle"
        aria-expanded={open}
        aria-label={open ? t('Close the editor', 'إغلاق المحرّر') : t('Open the editor', 'فتح المحرّر')}
        onClick={() => (open ? onMinimise() : selected && onSelect(selected))}
      >
        <span className="ed__grabber" aria-hidden="true" />
      </button>

      {open && theme && (
        <>
          <div className="ed__drawer__head">
            <h2 className="ed__drawer__title">{t('Colours', 'الألوان')}</h2>
          </div>
          <div className="ed__drawer__body ed__drawer__body--wide">
            <ThemePicker site={site} onApply={onApply} />
          </div>
        </>
      )}

      {open && !theme && block && def && (
        <>
          {/*
            ⚠️ THE HEAD BELONGS TO THE BLOCK, SO IT IS ABSENT WHEN THE BLOCK IS NOT WHAT IS SHOWING.
            It carries the section's name and the menu that duplicates and deletes it — both about the
            whole thing. Over one field it was two pieces of furniture above a single input: the name
            of a section the customer is looking at anyway, and an "All settings" button duplicating
            the Edit tab that is on the block three centimetres up the screen (§3.3). On a 360 px
            sheet that is a third of the drawer spent on chrome.

            > ⚠️ **IT USED TO SAY THE HEAD NAMES THE SECTION EVEN WHEN ONE FIELD IS SHOWING**, on the
            > argument that the field's own label is on the control below and "Headline" twice tells
            > nobody WHICH heading. The argument was right about the label and wrong about the
            > remedy: what says which heading is the page behind the drawer, with that heading
            > scrolled into view and outlined.
          */}
          {!field && (
          <div className="ed__drawer__head">
            <h2 className="ed__drawer__title">{r(def.label)}</h2>

            {onPage && (
              /*
               * ⚠️ THE SHARED `Menu`, NOT A SECOND COPY OF ONE. This was the same button, backdrop
               * and panel written out by hand, and the copy in `@ksa/ui` was added for the repeater
               * rows — at which point two implementations of one control is exactly the drift §0.5 is
               * about. The row menu then had to be portalled out of the scrolling drawer body to be
               * visible at all; this one lives in the head and never hit that, and would have been
               * the copy that quietly stayed broken the day the head started scrolling too.
               */
              <Menu
                up
                label={t('More', 'المزيد')}
                actions={[
                  {
                    label: t('Duplicate', 'تكرار'),
                    onClick: () => {
                      if (selected?.scope !== 'page') return
                      const copy = duplicateBlock(site, selected.pageId, selected.blockId)
                      if (!copy) return
                      onApply(copy.site)
                      onSelect(copy.ref)
                    },
                  },
                  'rule',
                  /*
                   * ⚠️ SEPARATED AND NAMED FOR WHAT IT REMOVES. `Delete` alone, one item below a
                   * harmless one in a thumb-opened menu, is a mis-tap that costs a section of
                   * somebody's page — and undo is in a different menu at the top of the screen.
                   */
                  {
                    label: t('Delete this section', 'حذف هذا القسم'),
                    danger: true,
                    onClick: () => {
                      if (selected?.scope !== 'page') return
                      onApply(removeBlock(site, selected.pageId, selected.blockId))
                      onSelect(null)
                    },
                  },
                ]}
              />
            )}
          </div>
          )}

          {/*
            ⚠️ THE FIELDS ARE DIRECT CHILDREN OF THE GRID, WITH NO WRAPPER. There was a `u-stack` here
            and it made the whole form ONE grid item — the columns were real, and a single cell held
            everything, so a 1280 px sheet still rendered five inputs a metre wide. A grid that
            silently has one child looks identical to a grid that was never applied.
          */}
          <div className="ed__drawer__body">
            {/* Layout and width belong to the block, so a single field never shows them. */}
            {!field && def.variants.length > 1 && (
              <Field label={t('Layout', 'التخطيط')}>
                {(a) => (
                  <Select
                    {...a}
                    value={block.variant ?? def.variants[0]!.value}
                    onChange={(e) => selected && onApply(setVariant(site, selected, e.target.value))}
                  >
                    {def.variants.map((v) => (
                      <option key={v.value} value={v.value}>
                        {r(v.label)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}

            {/*
              ⚠️ THE WIDTH CONTROL IS ABSENT FOR A `fullOnly` BLOCK RATHER THAN DISABLED. A hero or a
              call-to-action band always spans the page — that is declared on the block, and the
              editor reads the declaration rather than hard-coding a list. Showing a dead control
              would be offering a choice that does not exist.
            */}
            {!field && onPage && !def.fullOnly && (
              <Field label={t('Width', 'العرض')}>
                {(a) => (
                  <Select
                    {...a}
                    value={effectiveWidth(block, def)}
                    onChange={(e) =>
                      selected && onApply(setWidth(site, selected, e.target.value as (typeof BLOCK_WIDTHS)[number]))
                    }
                  >
                    <option value="full">{t('Full width', 'عرض كامل')}</option>
                    <option value="half">{t('Half', 'نصف')}</option>
                    <option value="third">{t('A third', 'ثلث')}</option>
                  </Select>
                )}
              </Field>
            )}

            {/*
              ⚠️ THE UPLOAD STATE IS SHOWN BESIDE THE FIELDS, NOT SWALLOWED. Preparing a photograph
              takes a second on a phone and uploading takes another; without this the customer picks
              a file, nothing visibly happens, and they pick it again. The failure is loud for the
              same reason — a picture that silently did not upload is one they will discover after
              publishing.
            */}
            {uploading && <Notice>{t('Preparing and uploading your picture…', 'جارٍ تجهيز صورتك ورفعها…')}</Notice>}
            {uploadFailure && (
              <Notice tone="error">{lang === 'ar' ? uploadFailure.text.ar : uploadFailure.text.en}</Notice>
            )}

            {showRow && rowSpec && row !== null ? (
              <ListItem
                spec={rowSpec}
                value={block.props[rowSpec.key]}
                index={row}
                ctx={ctx}
                onChange={(value) => selected && onApply(setProp(site, selected, rowSpec.key, value))}
                onMoved={(to) => onRow(to)}
                // Removing the row the sheet is showing leaves the sheet on the whole list, which is
                // where the customer can see what is left and add another.
                onRemoved={() => onRow(null)}
                /*
                 * ⚠️ THE ONE STEP THE PAGE CANNOT OFFER. Every other route out of this drawer is on
                 * the preview — the block has an Edit tab, each row is tappable — but "all of these
                 * rows" is a thing with no single element behind it to tap. It used to be a separate
                 * "All of these" button above a row of three unlabelled icons; it is now the Edit on
                 * the row's own «Branches: 2 of 3» line, which is the same shape as every other
                 * collapsed thing in this drawer.
                 */
                onAll={() => onRow(null)}
              />
            ) : (
              shownFields.map((spec) => (
                <Control
                  key={spec.key}
                  spec={spec}
                  value={block.props[spec.key]}
                  ctx={ctx}
                  onChange={(value) => selected && onApply(setProp(site, selected, spec.key, value))}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * ⚠️ THESE TWO NUMBERS ARE ALSO IN `editor.css`, ON `.ed__add`, AND THEY MUST AGREE. The button's
 * position is computed from a measured rectangle, so its own size has to be known before it is
 * rendered — there is no layout pass to ask. Both places say so; a disagreement is a plus sign
 * overlapping the word it belongs beside, which is visible immediately and only on the Arabic screen
 * if the sign of the offset is wrong.
 */
const ADD_SIZE = 26
const ADD_GAP = 6

/**
 * The colour schemes, as the colours rather than as a list of names.
 *
 * ⚠️ SWATCHES, NOT THE `<select>` THAT IS STILL IN THE RAIL. "Warm", "Cool", "Ink" are names for
 * things a customer can only judge by looking at them, and the dropdown made choosing a palette a
 * matter of reading five adjectives and pressing one. Here the choice is made of the actual colours,
 * and the page behind the drawer is repainted the moment one is pressed.
 *
 * ⚠️ THE COLOURS COME FROM `@ksa/render`'s TABLE, NOT FROM A COPY OF IT HERE. That table is what the
 * published page uses and what `check:contrast` measures; a hand-written set of hexes in the editor
 * would be a fourth place a palette lives and the first one to go stale.
 *
 * ⚠️ AND SWITCHING IS NON-DESTRUCTIVE, WHICH IS WHY IT CAN BE A TOY. `setTemplate` changes the token
 * set; a block's `tmpl`-scoped props fall back to the new template's defaults and every word the
 * customer wrote is untouched (see `prop-types.ts` on `Scope`). Undo is in the header if they want the
 * old one back.
 */
function ThemePicker({ site, onApply }: { site: Site; onApply: (site: Site) => void }) {
  const { t, r } = useLang()

  return (
    <div className="ed__themes">
      {Object.values(TEMPLATES).map((template) => {
        const current = template.id === site.template
        return (
          <button
            key={template.id}
            type="button"
            className={`ed__theme ${current ? 'ed__theme--current' : ''}`}
            aria-pressed={current}
            onClick={() => onApply(setTemplate(site, template.id))}
          >
            {/*
              ⚠️ THE THREE ROLES A VISITOR ACTUALLY SEES: the brand, the accent that sits on the
              buttons, and the paper everything is written on. A swatch of all eight roles is a colour
              chart; these three are what the page looks like from across a room.
            */}
            <span className="ed__theme__swatch" aria-hidden="true">
              <span style={{ background: template.colors.brand }} />
              <span style={{ background: template.colors.accent }} />
              <span style={{ background: template.colors.surface }} />
            </span>
            <span className="ed__theme__name">{r(template.label)}</span>
            <span className="ed__theme__mood">{r(template.mood)}</span>
          </button>
        )
      })}
      <p className="u-tiny u-faint">
        {t('Changing this never changes your words.', 'تغيير هذا لا يغيّر كلماتك.')}
      </p>
    </div>
  )
}

/**
 * Click-to-select over the preview.
 *
 * ⚠️ IT FINDS THE BLOCK BY WALKING UP TO THE ELEMENT THE RENDERER ALREADY LABELS. `SitePage` puts
 * each block's id on its wrapper — it does that anyway, because a link that says "scroll to the
 * contact section" needs an anchor target. Reusing it means the editor needs no second set of
 * markers in the renderer, and the renderer stays free of anything that exists only for the editor.
 */
function SelectionLayer({
  children,
  selected,
  site,
  pageId,
  blocks,
  labelFor,
  onSelect,
  onSelectField,
  onAddRow,
  onOpenBlock,
  onMove,
}: {
  children: React.ReactNode
  selected: BlockRef | null
  site: Site
  pageId: string
  blocks: { id: string; type: string }[]
  labelFor: (type: string) => string
  onSelect: (ref: BlockRef) => void
  onSelectField: (ref: BlockRef, key: string, index?: number) => void
  onAddRow: (ref: BlockRef, key: string) => void
  onOpenBlock: (ref: BlockRef) => void
  onMove: (ref: BlockRef & { scope: 'page' }, by: -1 | 1) => void
}) {
  const root = useRef<HTMLDivElement>(null)
  const { t, lang } = useLang()

  /** Where to hang the up/down buttons: the selected block's own box, in the page's coordinates. */
  const [orderBox, setOrderBox] = useState<{ top: number; left: number; width: number } | null>(null)
  /**
   * Where each repeater on the selected block wants its "add another" button.
   *
   * ⚠️ MEASURED FROM RECTANGLES RATHER THAN BY WALKING `offsetParent`, WHICH IS WHAT `orderBox` ABOVE
   * DOES. The order buttons hang off a block, whose offset parent is this wrapper; these hang off an
   * arbitrary heading several levels down, and the walk would have to survive every positioned
   * ancestor a template might introduce. Two rectangles subtracted are the same numbers with no walk
   * — both elements move together, so the difference is right whatever is scrolled.
   */
  const [addBoxes, setAddBoxes] = useState<{ key: string; top: number; left: number }[]>([])
  // The words stamped on every stand-in picture, in the language the BUILDER is in — this is our
  // chrome talking to the customer, not their site talking to a visitor.
  // ⚠️ AN INSTRUCTION, NOT A NOUN. "placeholder" names the thing and leaves the customer to work out
  // that it is their job; "replace this image" is the same warning and also the next step.
  const phLabel = t('replace this image', 'استبدل هذه الصورة')

  useEffect(() => {
    const element = root.current
    if (!element) return

    const onClick = (event: MouseEvent) => {
      // The preview's own links are already neutralised by `mode="preview"`; this stops a click on
      // one from also being swallowed by the browser's default.
      event.preventDefault()
      const hit = event.target as HTMLElement | null

      // ⚠️ THE MARK, NOT THE RULE. `.ed__hit` is put on the block elements by the effect above, which
      // is the one place that decides what a block is — see the note there for what having two of
      // those cost.
      const target = hit?.closest<HTMLElement>('.ed__hit')
      if (!target) return

      const ref: BlockRef =
        target.tagName === 'NAV'
          ? { scope: 'navbar' }
          : target.tagName === 'FOOTER'
            ? { scope: 'footer' }
            : { scope: 'page', pageId, blockId: target.id }
      if (ref.scope === 'page' && !ref.blockId) return

      /**
       * ⚠️ A FIELD ONLY COUNTS INSIDE THE BLOCK THAT IS ALREADY SELECTED. The highlights are only
       * drawn there, so a tap on an unselected block's heading is a tap on the block — which is what
       * it looks like to the customer, because nothing on that block was marked as editable yet.
       * Without this rule, one tap anywhere would open a form, and there would be no gesture left
       * that simply means "select this section".
       */
      const marked = hit?.closest<HTMLElement>('[data-ed-field]')
      if (marked && selected && sameRef(selected, ref)) {
        // ⚠️ THE ROW INDEX IS THE DOCUMENT'S, NOT THE SCREEN'S — see `listTargets.ts`. It is absent on
        // an ordinary field, which is exactly what "the whole field" means to `onSelectField`.
        const index = marked.dataset.edRow
        return onSelectField(ref, marked.dataset.edField ?? '', index === undefined ? undefined : Number(index))
      }

      onSelect(ref)
    }

    element.addEventListener('click', onClick)
    return () => element.removeEventListener('click', onClick)
  }, [onSelect, onSelectField, pageId, selected])

  // The outline is applied by id rather than by cloning children, so the renderer's tree is
  // untouched — the preview really is the published markup.
  /**
   * Which elements ARE blocks — marked once, read by everything else.
   *
   * ⚠️ THIS EXISTS BECAUSE THE ANSWER WAS WRITTEN DOWN TWICE AND THE TWO COPIES DISAGREED. The click
   * handler and the hover outline both said "a child of the flow, or a `nav`, or a `footer`" — and
   * the footer renders its links inside a `<nav aria-label="Footer">`. In JavaScript that made a tap
   * on a footer link select the navbar; in CSS it drew a second dashed box around the footer's links
   * while the footer they are in was already selected. Same wrong rule, two symptoms, and fixing one
   * left the other (CLAUDE.md §0.5).
   *
   * ⚠️ THE RULE IS "OUTERMOST", and that is what the nesting check is. Blocks are siblings and never
   * nest, so anything matching inside another match is markup a component chose — not a block.
   */
  useEffect(() => {
    const element = root.current
    if (!element) return
    const candidates = element.querySelectorAll<HTMLElement>('[id^="blk_"], nav, footer')
    for (const candidate of candidates) {
      const nested = candidate.parentElement?.closest('[id^="blk_"], nav, footer')
      candidate.classList.toggle('ed__hit', !nested || !element.contains(nested))
    }
  }, [blocks, children])

  useEffect(() => {
    const element = root.current
    if (!element) return
    for (const previous of element.querySelectorAll('.ed__block--selected')) {
      previous.classList.remove('ed__block--selected')
    }
    if (!selected) return
    const target =
      selected.scope === 'page'
        ? element.querySelector(`#${CSS.escape(selected.blockId)}`)
        : element.querySelector(selected.scope === 'navbar' ? 'nav.ed__hit' : 'footer.ed__hit')
    if (!(target instanceof HTMLElement)) return
    target.classList.add('ed__block--selected')
  }, [selected, children])

  /**
   * Mark what is separately editable inside the selected block, and where to put the order buttons.
   *
   * ⚠️ ONLY THE SELECTED BLOCK IS MARKED. Highlighting every editable string on the page at once
   * turns the preview into a wireframe of dashed boxes — the customer stops seeing their site and
   * starts seeing the editor, which is the failure this whole layout is trying to avoid.
   *
   * ⚠️ AND THE MARK IS AN ATTRIBUTE PLUS A CLASS ON WHAT THE RENDERER ALREADY PRODUCED. Nothing is
   * inserted into the tree: an injected element would land inside `textContent`, which the
   * empty-block detector below measures, and a selected empty section would stop being empty and
   * lose the height that makes it tappable.
   */
  useEffect(() => {
    const element = root.current
    if (!element) return

    for (const previous of element.querySelectorAll<HTMLElement>('[data-ed-field]')) {
      previous.classList.remove('ed__field', 'ed__field--row')
      delete previous.dataset.edField
      delete previous.dataset.edRow
    }
    if (!selected) {
      setOrderBox(null)
      setAddBoxes([])
      return
    }

    const blockEl =
      selected.scope === 'page'
        ? element.querySelector<HTMLElement>(`#${CSS.escape(selected.blockId)}`)
        : element.querySelector<HTMLElement>(selected.scope === 'navbar' ? 'nav' : 'footer')
    if (!blockEl) {
      setOrderBox(null)
      setAddBoxes([])
      return
    }

    const block = getBlock(site, selected)
    const def = block ? getBlockDef(block.type) : undefined
    const boxes: { key: string; top: number; left: number }[] = []

    if (block && def) {
      for (const target of fieldTargets(blockEl, block, def, lang)) {
        target.element.dataset.edField = target.key
        target.element.classList.add('ed__field')
      }

      /**
       * ⚠️ THE ROWS ARE MARKED AFTER THE FIELDS, SO THE INNER MARK WINS. `closest('[data-ed-field]')`
       * walks outwards from what was tapped, and a row's own element is deeper than the list it is in
       * — which is why tapping «Friday» opens Friday rather than the block. The two passes cannot
       * collide over one element: a field's string is the block's, a row's string is the row's.
       *
       * ⚠️ AND THE PLUS IS MEASURED HERE RATHER THAN POSITIONED IN CSS, because there is nowhere in
       * the customer's markup to put it. Everything the editor draws over the page is a sibling of the
       * page (CLAUDE.md §3.3): an element inserted into a block lands in the `textContent` the
       * empty-block detector measures.
       */
      const rootRect = element.getBoundingClientRect()
      for (const list of listTargets(blockEl, block, def, lang)) {
        list.rows.forEach((rowEl, index) => {
          if (!rowEl) return
          rowEl.dataset.edField = list.key
          rowEl.dataset.edRow = String(index)
          rowEl.classList.add('ed__field', 'ed__field--row')
        })

        if (!list.anchor) continue
        /*
         * ⚠️ THE TEXT'S BOX FOR A HEADING, THE ELEMENT'S BOX FOR A CONTAINER. A heading is a block
         * element as wide as its column, so its own rectangle puts the button at the far edge of the
         * page — nowhere near the words it belongs to. A `Range` over its contents measures where
         * «Opening hours» actually ENDS, which is where a person would point.
         */
        let rect: DOMRect
        if (list.anchorIsHeading) {
          const range = document.createRange()
          range.selectNodeContents(list.anchor)
          rect = range.getBoundingClientRect()
          range.detach()
        } else {
          rect = list.anchor.getBoundingClientRect()
        }
        if (rect.width === 0 && rect.height === 0) continue

        /*
         * ⚠️ THE SIDE IS CHOSEN IN JAVASCRIPT BECAUSE THE POSITION IS A NUMBER, and a number has no
         * writing direction. Everything else in this codebase mirrors through logical properties;
         * absolute coordinates cannot, so the direction is named here, once, rather than in a
         * stylesheet where §2.6's grep gate would rightly refuse it.
         */
        const rtl = lang === 'ar'
        let top = rect.top - rootRect.top
        let left = (rtl ? rect.left - ADD_GAP - ADD_SIZE : rect.right + ADD_GAP) - rootRect.left

        /*
         * ⚠️ IF IT DOES NOT FIT BESIDE THE THING, IT GOES UNDER IT — because a button drawn outside
         * the block is a button nobody can press. The preview is clipped at the frame, so a `+` a few
         * pixels past the edge is not merely awkward, it is GONE: on a phone the nav bar's `+` sat at
         * x=397 of a 412 px screen and simply could not be seen or tapped, which reads as the feature
         * not existing rather than as a misplacement.
         *
         * Under it, at its own inline start, is where a fourth row would appear anyway.
         */
        const room = rootRect.width - ADD_SIZE
        if (left > room || left < 0) {
          top = rect.bottom - rootRect.top + ADD_GAP
          left = (rtl ? rect.right - ADD_SIZE : rect.left) - rootRect.left
        }

        boxes.push({ key: list.key, top, left: Math.max(0, Math.min(left, room)) })
      }
    }

    setAddBoxes(boxes)

    /*
     * ⚠️ MEASURED AGAINST THIS WRAPPER, NOT THE VIEWPORT, so the buttons scroll with the block
     * instead of needing a scroll listener to chase it. `offsetTop` walks up to the nearest
     * positioned ancestor, which `editor.css` makes this element.
     */
    let top = 0
    let left = 0
    for (let node: HTMLElement | null = blockEl; node && node !== element; node = node.offsetParent as HTMLElement | null) {
      top += node.offsetTop
      left += node.offsetLeft
    }
    setOrderBox({ top, left, width: blockEl.offsetWidth })
  }, [selected, site, lang, children])

  /**
   * Label the sections that have nothing in them yet.
   *
   * ⚠️ THE LABEL COMES FROM THE BLOCK'S REGISTRY ENTRY, so a fourteenth block gets one without any
   * code here changing — and a published page never sees it, because it is written on by the editor
   * after the renderer has produced its tree rather than by the renderer itself. Without it a fresh
   * template is four coloured bands and no way to tell which is the gallery except by clicking.
   */
  useEffect(() => {
    const element = root.current
    if (!element) return
    for (const block of blocks) {
      const wrapper = element.querySelector(`#${CSS.escape(block.id)}`)
      if (!(wrapper instanceof HTMLElement)) continue
      // "Empty" is decided from what was actually rendered, not from the props — a block whose only
      // filled field is an image is not empty, and a block whose heading is whitespace is.
      const empty = (wrapper.textContent ?? '').replace(/[\s⋯]/g, '').length === 0
      if (empty) wrapper.dataset.empty = labelFor(block.type)
      else delete wrapper.dataset.empty
    }
  }, [blocks, labelFor, children])

  /**
   * Put a blurred picture behind every image that does not exist yet.
   *
   * ⚠️ WRITTEN ONTO THE DOM HERE, NEVER RENDERED BY `@ksa/render`, AND THAT IS THE WHOLE DESIGN.
   * §2.2 makes the renderer shared so the preview is what publishes; a stock photograph behind every
   * empty slot is emphatically NOT what publishes. Painting it from the editor after the fact means
   * the published page cannot inherit it even by accident — the same reason the empty-block label
   * above is here rather than in the renderer.
   *
   * ⚠️ AND IT IS BLURRED, DESATURATED AND LABELLED, WHICH IS NOT A STYLE CHOICE. This is the one
   * editing aid that could be mistaken for content: a customer who thinks those are their photographs
   * publishes a page of empty boxes and finds out from a visitor. It has to look unmistakably
   * temporary, so `editor.css` blurs it hard and stamps a word on it in the customer's own language.
   *
   * The description drives the choice, so a coffee shop gets the coffee picture — see
   * `pickPlaceholder`. The block id and field index seed the fallback, so a slot that matches nothing
   * keeps ONE picture rather than reshuffling on every keystroke.
   */
  useEffect(() => {
    const element = root.current
    if (!element) return

    /*
     * ⚠️ THE PIXELS ARE FETCHED, NOT IMPORTED, AND THE STAMP IS PAINTED BEFORE THEY ARRIVE. The
     * gallery is ~300 kB of base64 and now loads as its own chunk (see `loadPlaceholderArt`), so
     * there is a moment — one chunk, from cache after the first time — with no picture. What must
     * NOT wait for it is the dashed box and the words on it: §5.11 says three signals say "not
     * yours", and a box that is briefly unmarked is a box that briefly looks like a real empty
     * space in the customer's design.
     *
     * ⚠️ AND THE EFFECT MAY HAVE BEEN TORN DOWN BY THEN. `cancelled` is what stops a resolved import
     * writing a background onto elements from a page the customer has already navigated away from.
     */
    let cancelled = false
    const boxes = element.querySelectorAll('.s-placeholder')

    const chosen: { box: HTMLElement; id: string }[] = []
    boxes.forEach((box, index) => {
      if (!(box instanceof HTMLElement)) return
      // ⚠️ THE SURROUNDING BLOCK'S WORDS, BECAUSE THE BOX NO LONGER HAS ANY. It used to hold the
      // model's description of the picture, which was the perfect signal — and that description is
      // gone with the generation feature. The nearest block's heading and copy is what is left, and
      // it is a good signal: a section about coffee gets the coffee picture.
      const owner = box.closest('.s-flow > *, nav, footer') ?? box
      const description = owner.textContent ?? ''
      const picture = pickPlaceholder(description, `${element.id || 'blk'}:${index}`)
      box.dataset.phLabel = phLabel

      /*
       * ⚠️ A PLACEHOLDER THAT IS A BACKGROUND CANNOT CARRY ITS OWN STAMP, AND BOTH REASONS ARE FATAL.
       * The overlay hero draws its picture in a layer at `opacity: 0.28` with the copy on top — so the
       * words «replace this image» came out at 28% of their colour, unreadable, and they landed dead
       * centre, which is exactly where the headline and the supporting line are. §5.11 says three
       * signals say "not yours" and the words are the one doing most of the work; there, the words
       * were doing none.
       *
       * So the stamp moves to the SECTION, outside the dimming, out of the copy's way. The class
       * named here is what one renderer component happens to emit — the same kind of editor knowledge
       * about rendered markup that lives in `listTargets.ts`, and it is in the app for the same
       * reason: `@ksa/render`'s stylesheet is not something `@ksa/model` may depend on.
       */
      if (box.closest('.s-hero__overlay-image') && owner instanceof HTMLElement) {
        owner.dataset.phOver = phLabel
        box.dataset.phQuiet = 'true'
      }

      chosen.push({ box, id: picture.id })
    })

    void loadPlaceholderArt().then((art) => {
      if (cancelled) return
      for (const { box, id } of chosen) {
        const src = art[id]
        if (src) box.style.setProperty('--ed-ph', `url("${src}")`)
      }
    })

    return () => {
      cancelled = true
    }
  }, [blocks, children, phLabel])

  const canOrder = selected?.scope === 'page'
  const index = canOrder ? blocks.findIndex((b) => b.id === selected.blockId) : -1

  return (
    <div ref={root} className="ed__block">
      {children}

      {/*
        ⚠️ "ANOTHER ONE OF THESE", BESIDE THE THING THERE ARE SEVERAL OF. Adding an opening-hours row
        used to mean opening the block's whole form and finding the list in it; the customer is
        already pointing at the hours. Where each one lands is declared per block in `listTargets.ts`
        — beside «Opening hours» for `contact`, beside the section heading for `features`.
      */}
      {selected &&
        addBoxes.map((box) => (
          <button
            key={box.key}
            type="button"
            className="ed__add"
            style={{ top: box.top, left: box.left }}
            aria-label={t('Add another', 'أضف واحدًا آخر')}
            title={t('Add another', 'أضف واحدًا آخر')}
            onClick={() => onAddRow(selected, box.key)}
          >
            +
          </button>
        ))}

      {/*
        ⚠️ UP AND DOWN ARE ON THE BLOCK, OPPOSITE THE "EDIT" TAB, AND THEY USED TO BE ON THE DRAWER.
        Reordering is the commonest edit anybody makes and it is about the block — so it belongs
        where the block is, not on a bar at the bottom of the screen that the customer has to look
        away to reach. It is also what let the drawer become an actual handle.

        ⚠️ ABSOLUTELY POSITIONED OVER THE BLOCK RATHER THAN INSIDE IT. These are real buttons and a
        pseudo-element cannot be one; putting them in the customer's markup would change its
        `textContent` and break the empty-block detector, exactly as the Edit tab must not.

        ⚠️ THE BOX SPANS THE BLOCK AND THE BUTTONS SIT AT ITS FLEX END, so they land top-right in
        English and top-left in Arabic without either side being named. The offsets are geometry,
        not authored direction.
      */}
      {orderBox && selected && (
        <div className="ed__over" style={{ top: orderBox.top, left: orderBox.left, width: orderBox.width }}>
          {/*
            ⚠️ THE "EDIT" TAB IS A REAL BUTTON HERE, AND IT WAS A `::after` PSEUDO-ELEMENT. The
            pseudo-element existed to avoid putting a node inside the customer's block — an injected
            element lands in `textContent`, which the empty-block detector measures, and a selected
            empty section would stop being empty and lose the height that makes it tappable. This
            overlay is a sibling OF the page rather than a child of a block, so that problem is gone
            and the tab can be what it always looked like: a button.
          */}
          <button type="button" className="ed__tab" onClick={() => onOpenBlock(selected)}>
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" focusable="false">
              <path
                d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('Edit', 'تعديل')}
          </button>

          <div className="u-spacer" />

          {/*
            ⚠️ UP AND DOWN ARE ON THE BLOCK, OPPOSITE THE TAB, AND THEY USED TO BE ON THE DRAWER.
            Reordering is the commonest edit anybody makes and it is about the block — so it belongs
            where the block is, not on a bar at the bottom that the customer has to look away to
            reach. It is also what let the drawer become an actual handle.

            ⚠️ THE BOX SPANS THE BLOCK AND THESE SIT AT ITS FLEX END, so they land top-right in
            English and top-left in Arabic without either side being named. The offsets are geometry,
            not authored direction.
          */}
          {canOrder && (
            <>
              <button
                type="button"
                className="ed__order__btn"
                aria-label={t('Move up', 'لأعلى')}
                disabled={index <= 0}
                onClick={() => selected.scope === 'page' && onMove(selected, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="ed__order__btn"
                aria-label={t('Move down', 'لأسفل')}
                disabled={index < 0 || index >= blocks.length - 1}
                onClick={() => selected.scope === 'page' && onMove(selected, 1)}
              >
                ↓
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/*
 * > ⚠️ **A `SaveState` COMPONENT STOOD HERE AND WAS DEAD CODE — nothing had rendered it since the
 * > editor's two bars became one menu in the header.** It is deleted rather than extended for the
 * > fifth save state, because a second place that puts the save state into words is a second place
 * > that can disagree with the first. The wording, and the reason "Saved" must never be read as
 * > "published", live in `editorChrome.tsx` where the only surviving indicator is.
 */

function PageSettings({ site, pageId, onChange }: { site: Site; pageId: string; onChange: (s: Site) => void }) {
  const { t, r } = useLang()
  const page = site.pages.find((p) => p.id === pageId)
  if (!page) return null
  const state = slugState(site, page.id, page.slug)
  const isHome = site.pages[0]?.id === page.id
  const isLast = site.pages.length <= 1

  return (
    <div className="u-stack u-stack--tight">
      <Field label={t('Page name', 'اسم الصفحة')}>
        {(a) => <Input {...a} value={r(page.title)} onChange={(e) => onChange(setPageTitle(site, page.id, { ...(page.title as { en: string; ar: string }), en: e.target.value }))} />}
      </Field>

      <Field
        label={t('Address', 'المسار')}
        hint={isHome ? t('The home page is served at /', 'الصفحة الرئيسية تُقدَّم على /') : undefined}
        {...(state === 'malformed'
          ? { error: t('Lowercase English letters, digits and hyphens.', 'أحرف إنجليزية صغيرة وأرقام وشرطات.') }
          : state === 'duplicate'
            ? { error: t('Another page already uses this address.', 'صفحة أخرى تستخدم هذا المسار.') }
            : {})}
      >
        {(a) => (
          <Input
            {...a}
            dir="ltr"
            value={page.slug}
            invalid={state !== 'ok'}
            onChange={(e) => onChange(setPageSlug(site, page.id, e.target.value))}
          />
        )}
      </Field>

      {/*
        ⚠️ DISABLED WITH THE REASON VISIBLE, NOT HIDDEN. `removePage` refuses the last page in the op
        itself, so no caller can reach an empty site; this control shows why it is off rather than
        vanishing, because a control that disappears reads as a bug.
      */}
      <Button variant="ghost" size="sm" disabled={isLast} onClick={() => onChange(removePage(site, page.id))}>
        {t('Delete this page', 'حذف هذه الصفحة')}
      </Button>
      {isLast && <p className="u-tiny u-faint">{t('A site needs at least one page.', 'الموقع يحتاج صفحة واحدة على الأقل.')}</p>}
    </div>
  )
}

function SiteSettings({ site, onChange }: { site: Site; onChange: (s: Site) => void }) {
  const { t, r } = useLang()
  return (
    <div className="u-stack u-stack--tight">
      <Field
        label={t('Look', 'المظهر')}
        hint={t('Changing this never changes your words.', 'تغيير هذا لا يغيّر كلماتك.')}
      >
        {(a) => (
          <Select {...a} value={site.template} onChange={(e) => onChange(setTemplate(site, e.target.value))}>
            {Object.values(TEMPLATES).map((template) => (
              <option key={template.id} value={template.id}>
                {r(template.label)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label={t('Main language', 'اللغة الرئيسية')}
        hint={t('Which language visitors see first. Both are always published.', 'اللغة التي يراها الزائر أولًا. تُنشر اللغتان دائمًا.')}
      >
        {(a) => (
          <Select {...a} value={site.defaultLang} onChange={(e) => onChange(setDefaultLang(site, e.target.value as Lang))}>
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </Select>
        )}
      </Field>
    </div>
  )
}

export { refsEqual }
